import React, { useState, useEffect } from 'react';
import { Card, Table, Pagination, Input, Select, message } from 'antd';

const { Option } = Select;

const defaultState = { data: [], meta: { total: 0, page: 1, page_size: 5 } };

const MonitorCard = ({ 
  title, 
  columns,
  fetchFunction,
  pollInterval = 300000,
  filters,
  initialFilterValues = {},
  onFilterChange,
  onSearch
}) => {
  const [loading, setLoading] = useState(true);
  const [cardData, setCardData] = useState(defaultState);
  const [filterValues, setFilterValues] = useState(initialFilterValues);

  const buildFilters = (values) => {
    const result = {};
    for (const f of (filters || [])) {
      const v = values[f.field];
      if (v) result[f.field] = v;
    }
    return result;
  };

  const doFetch = async (pageNumber, pageSize, extraFilters) => {
    try {
      setLoading(true);
      const response = await fetchFunction({ page: pageNumber, pageSize, ...extraFilters });
      const formattedData = formatResponse(response, pageSize);
      setCardData(formattedData);
    } catch (error) {
      console.error(`Failed to fetch data for ${title}:`, error);
      message.error(`${title}数据加载失败`);
    } finally {
      setLoading(false);
    }
  };

  const formatResponse = (res, pageSize) => {
    if (!res) return defaultState;
    if (res.data) {
      return {
        data: Array.isArray(res.data) ? res.data : [],
        meta: res.meta || { total: 0, page: 1, page_size: pageSize }
      };
    }
    return {
      data: Array.isArray(res) ? res : [],
      meta: { total: res.length || 0, page: cardData.meta?.page || 1, page_size: pageSize }
    };
  };

  useEffect(() => {
    doFetch(1, cardData.meta?.page_size || 5, buildFilters(filterValues));
  }, []);

  useEffect(() => {
    const interval = setInterval(
      () => doFetch(cardData.meta?.page, cardData.meta?.page_size, buildFilters(filterValues)),
      pollInterval
    );
    return () => clearInterval(interval);
  }, [cardData.meta?.page, cardData.meta?.page_size]);

  const handlePageChange = (page, pageSize) => {
    doFetch(page, pageSize, buildFilters(filterValues));
  };

  const handleShowSizeChange = (current, size) => {
    doFetch(1, size, buildFilters(filterValues));
  };

  const handleFilterChange = (field, value) => {
    const next = { ...filterValues, [field]: value || undefined };
    if (!value) delete next[field];
    setFilterValues(next);

    if (onFilterChange) onFilterChange(field, value);

    const filtersForFetch = buildFilters(next);
    if (onSearch) onSearch(filtersForFetch);
    doFetch(1, cardData.meta?.page_size || 5, filtersForFetch);
  };

  return (
    <Card 
      title={title}
      style={{ marginBottom: 16 }}
      extra={
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {(filters || []).map((f) => f.suggestions ? (
            <Select
              key={f.field}
              showSearch
              allowClear
              placeholder={f.placeholder}
              value={filterValues[f.field] || undefined}
              onChange={(value) => handleFilterChange(f.field, value)}
              style={{ width: 160 }}
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {f.suggestions.map((s) => (
                <Option key={s} value={s}>{s}</Option>
              ))}
            </Select>
          ) : (
            <Input
              key={f.field}
              placeholder={f.placeholder}
              allowClear
              value={filterValues[f.field] || ''}
              onChange={(e) => handleFilterChange(f.field, e.target.value)}
              style={{ width: 160 }}
            />
          ))}
          <Pagination
            current={cardData.meta?.page || 1}
            pageSize={cardData.meta?.page_size || 5}
            total={cardData.meta?.total || 0}
            showSizeChanger
            onChange={handlePageChange}
            onShowSizeChange={handleShowSizeChange}
          />
        </div>
      }
    >
      <div style={{ overflowX: 'auto' }}>
        <Table
          columns={columns}
          dataSource={cardData.data || []}
          loading={loading}
          pagination={false}
          size="small"
          rowKey="id"
          scroll={{ x: 'max-content' }}
        />
      </div>
    </Card>
  );
};

export default MonitorCard;